import React, { useContext, useEffect, useState, useRef } from "react";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import Box from "@material-ui/core/Box";
import CustomDialog, { CustomDialogProps } from "../../../../../components/Dialog/CustomDialog";
import TestSeriesApi from "../../../../../api/services/TestSeriesApi";
import CustomButton from "../../../../../components/Button/CustomButton";
import CustomProgress from "../../../../../components/Alert/CustomProgress";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import ConfirmationDialogContext from "../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { tesaBlue, tesaDarkRedError } from "../../../../../styles/colors";
import CustomTextFieldDynamic from "../../../../../components/Input/CustomTextFieldDynamic";
import NoteHeader from "./NoteHeader";
import useEnv from "../../../../../hooks/env";
import { TestSeriesContext } from "../../../../../contexts/TestSeriesContext";
import ConfidentialConfirmationDialog from "../../../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../../../functions/confidentialCheck.function";

interface TestCasesNotesDetailDialogProps extends CustomDialogProps {
  selectedTestCaseId: string | number;
  selectedTestCaseIndex?: number;
  testSeriesId: number;
  testSeriesIndex: number;
}

const TestCasesNotesDetailDialog = (props: TestCasesNotesDetailDialogProps) => {
  const { onClose, open, selectedTestCaseId, selectedTestCaseIndex, testSeriesIndex } = props;
  const { api } = useEnv();
  const testSeriesApi = new TestSeriesApi(api);
  const { permissions, selectedTestSeriesObject } = useContext(TestSeriesContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const formRef = useRef<FormikValues>();

  const [notes, setNotes] = useState<TestRequestRaw.Schemas.TestCaseNoteListResponse[]>(
    [] as TestRequestRaw.Schemas.TestCaseNoteListResponse[],
  );
  const [deletionQueue, setDeletionQueue] = useState<number[]>([]);
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingValues, setPendingValues] = useState<FormikValues | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);

  const fetchData = () => {
    testSeriesApi
      .getTestCaseNotes(selectedTestSeriesObject.id, selectedTestCaseId as number)
      .then((res) => {
        setNotes(res.data);
      })
      .catch((err) => {
        if (err) {
          handleHttpError(err);
        }
      });
  };

  const initialValuesArr = notes.map((note) => {
    const stringId = (note.id as number).toString();
    return { [stringId]: note.note };
  });

  const toObject = (arr: any[]) => {
    const rv = {};
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < arr.length; ++i) {
      // @ts-ignore
      rv[i] = arr[i];
    }
    return rv;
  };

  const initialValues = toObject(initialValuesArr);

  const formatValuesForApi = (values: FormikValues) => {
    const formattedNotes = notes
      .map((note, index) => {
        if (!deletionQueue.includes(note.id as number)) {
          return {
            newText: values[index.toString() || note.note || ""][note.id as number],
            noteId: note.id,
          };
        }
        return null;
      })
      .filter((note) => note !== null);

    return {
      removes: [...deletionQueue],
      updates: [...formattedNotes],
    };
  };

  const handleSaveAndClose = (values: FormikValues) => {
    // Check all note texts for confidential info
    const noteTexts = notes.map((note, index) => {
      try {
        return note.id !== undefined ? values[index.toString()][note.id] : "";
      } catch {
        return "";
      }
    });
    if (confidentialRegex && containsHighlyConfidentialInfo(noteTexts, confidentialRegex)) {
      setPendingValues(values);
      setShowConfidentialDialog(true);
      return;
    }

    const apiPayload = formatValuesForApi(values);

    testSeriesApi
      .updateNoteToTestCases(
        selectedTestSeriesObject.id,
        selectedTestCaseId as number,
        apiPayload as TestRequestRaw.Schemas.UpdateTestCaseNotes,
      )
      .then(() => {
        showSnackbar("Notes saved successfully", "success");
        onClose();
      })
      .catch((err) => {
        if (err) {
          handleHttpError(err);
        }
      });
  };

  const handleClose = () => {
    // @ts-ignore
    if (formRef.current?.dirty || deletionQueue.length > 0) {
      showConfirmationDialog({
        headline: "YOU HAVE UNSAVED CHANGES",
        text: "If you continue, your current changes to the test case notes will be discarded",
        confirmText: "Save",
        confirmColor: tesaBlue,
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      })
        .then((id: string) => {
          if (id === "DISCARD") {
            onClose();
          } else {
            handleSaveAndClose(formRef.current?.values);
          }
        })
        .catch(() => {
          // do nothing on cancel
        });
    } else {
      onClose();
    }
  };

  const queueForDeletion = (noteId: number) => {
    setDeletionQueue((prevState) => [...prevState, noteId]);
  };

  useEffect(() => {
    if (selectedTestCaseId) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  if (notes.length === 0 && open) {
    return <CustomProgress />;
  }

  return (
    <>
      <CustomDialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <h1>
          NOTES TO TEST ID {testSeriesIndex}-{selectedTestCaseIndex}
        </h1>
        <Formik
          // @ts-ignore
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={(values) => {
            if (formRef.current?.dirty || deletionQueue.length > 0) {
              handleSaveAndClose(values);
            } else {
              onClose();
            }
          }}
        >
          {(formikProps: FormikProps<{ noteId: number | null; text: string }[] | {}>): JSX.Element => {
            // formik inner jsx form
            const { isSubmitting, values, setFieldValue, dirty } = formikProps;
            return (
              <Form>
                {notes.map((note, index) => {
                  const testIndexes = note.testCases?.map((testCase) => testCase.index);

                  return (
                    <CustomTextFieldDynamic
                      editPermission={permissions.editResultAttachmentsAndNotes}
                      header={<NoteHeader seriesIndex={testSeriesIndex} testIndexes={testIndexes as number[]} />}
                      helperText="Changes to the Note will be effective for each Test ID"
                      hide={deletionQueue.includes(note.id as number)}
                      // @ts-ignore
                      id={index}
                      key={note.id}
                      name={index.toString()}
                      onChange={(e) => {
                        setFieldValue(index.toString(), {
                          [note.id?.toString() as string]: e.target.value,
                        });
                      }}
                      onDelete={() => {
                        queueForDeletion(note.id as number);
                      }}
                      testIndexes={testIndexes as number[]}
                      // @ts-ignore
                      value={values[index][note.id]}
                    />
                  );
                })}
                <Box textAlign="right" marginTop="1rem">
                  <CustomButton
                    primary
                    text={dirty || deletionQueue.length > 0 ? "Save and Close" : "Close"}
                    type="submit"
                    disabled={isSubmitting}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </CustomDialog>
      {showConfidentialDialog && (
        <ConfidentialConfirmationDialog
          open={showConfidentialDialog}
          objectName="note"
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
          onProceed={() => {
            if (pendingValues) {
              const apiPayload = formatValuesForApi(pendingValues);
              testSeriesApi
                .updateNoteToTestCases(
                  selectedTestSeriesObject.id,
                  selectedTestCaseId as number,
                  {
                    ...apiPayload,
                    confidentialityLevel: "HIGHLY_CONFIDENTIAL",
                  } as TestRequestRaw.Schemas.UpdateTestCaseNotes,
                )
                .then(() => {
                  showSnackbar("Notes saved successfully", "success");
                  onClose();
                })
                .catch(handleHttpError);
            }
            setShowConfidentialDialog(false);
            setPendingValues(null);
          }}
        />
      )}
    </>
  );
};

export default TestCasesNotesDetailDialog;
